<template>
  <span v-if="!cell" class="fu-dc__muted">—</span>

  <div v-else-if="col === 'status'" class="fu-dc__status" data-test="fu-cell-status">
    <span class="fu-dc__statusline">
      <span class="fu-dc__dot" :class="`fu-dc__dot--${cell.category || 'none'}`" aria-hidden="true"></span>
      <span v-if="cell.label" class="fu-dc__statuslabel" :title="cell.label">{{ cell.label }}</span>
      <span v-else class="fu-dc__muted">—</span>
    </span>
    <div v-if="cell.sub" class="fu-dc__statussub">
      <fu-icon name="cal" :size="13" />
      {{ cell.sub }}
    </div>
  </div>

  <div v-else-if="col === 'att'" data-test="fu-cell-attempts">
    <span
      v-if="cell.has"
      v-b-tooltip.hover.focus
      class="fu-dc__att"
      tabindex="0"
      :title="cell.tip"
      :aria-label="cell.tip"
    >
      <fu-icon v-for="(k, i) in cell.icons" :key="i" :name="ATTEMPT_ICON[k]" :size="14" />
      <span v-if="cell.more" class="fu-dc__more">{{ cell.more }}</span>
    </span>
    <span v-else class="fu-dc__muted">None yet</span>
  </div>

  <div v-else-if="col === 'rem'" data-test="fu-cell-reminders">
    <span
      v-if="cell.zero"
      v-b-tooltip.hover.focus
      class="fu-dc__rem"
      tabindex="0"
      :title="cell.tooltip"
      :aria-label="`${cell.count || 0} reminders. ${cell.tooltip}`"
      :data-hint="cell.warn ? 'warn' : 'pending'"
    >
      {{ cell.count }}
      <fu-icon name="clock" :size="14" :class="cell.warn ? 'fu-dc__clock--warn' : 'fu-dc__clock--pending'" />
    </span>
    <span v-else class="fu-dc__rem">{{ cell.count }}</span>
  </div>

  <div v-else-if="col === 'proc'" class="fu-dc__proc">
    <span v-if="cell.code" class="fu-dc__code">{{ cell.code }}</span>
    <span class="fu-dc__main">{{ cell.desc }}</span>
  </div>

  <div v-else-if="isMoney" class="fu-dc__money">
    <div class="fu-dc__amount">{{ cell.main }}</div>
    <div v-if="cell.sub" class="fu-dc__sub">{{ cell.sub }}</div>
  </div>

  <span v-else-if="cell.muted" class="fu-dc__muted">{{ cell.muted }}</span>

  <div v-else>
    <div class="fu-dc__main">{{ cell.main }}</div>
    <div v-if="cell.sub" class="fu-dc__sub">
      {{ cell.sub }}<template v-if="cell.yes"> <span class="fu-dc__yes">Yes</span></template><template v-if="cell.no"> <span class="fu-dc__no">No</span></template>
    </div>
    <div v-if="cell.subDanger" class="fu-dc__sub fu-dc__sub--danger">{{ cell.subDanger }}</div>
  </div>
</template>

<script>
/**
 * Every data cell that is not the patient, the follow-up, a checkbox or the remove button — one
 * switch on the column key, drawing the cell model rowModel.js built (`item.cells[col]`):
 *   status   the category dot + label; "Apt on Schedule" adds the visit date under it
 *   att      up to three channel icons and "+N", the tally as the tooltip
 *   rem      auto reminders sent — a 0 says whether it is "not yet" or "never" (reminderHint.js)
 *   proc     procedure / adjustment code + description
 *   money    txamt / claimfee / balance / fee — right-aligned, display face
 *   text     everything else: main line, sub line (with the 2×/yr Yes/No), red sub (claims > 90 days)
 */
import FuIcon from './FuIcon.vue';

const MONEY = ['txamt', 'claimfee', 'balance', 'fee'];

export default {
  name: 'FollowupsDataCell',
  components: { FuIcon },
  props: {
    col: { type: String, required: true },
    cell: { type: Object, default: null },
    rowId: { type: String, default: null },
  },
  data() {
    return { ATTEMPT_ICON: { call: 'phone', text: 'text', email: 'mail' } };
  },
  computed: {
    isMoney() {
      return MONEY.indexOf(this.col) !== -1;
    },
  },
};
</script>

<style lang="scss" scoped>
@import './mixins';

.fu-dc__main {
  font-size: 13.5px;
  font-weight: $fu-fw-semibold;
  line-height: 1.35;
  overflow-wrap: anywhere;
}

.fu-dc__sub {
  font-size: 12px;
  color: $fu-text-muted;
  margin-top: 2px;
  line-height: 1.35;
}

.fu-dc__sub--danger {
  color: $fu-danger-text;
  font-weight: $fu-fw-semibold;
}

// The space before "Yes" / "No" is a margin: the build's whitespace: 'condense' drops the
// template's lone space between `{{ cell.sub }}` and the span ("2×/yr preventiveYes").
.fu-dc__yes {
  margin-left: 4px;
  color: $fu-success;
  font-weight: $fu-fw-bold;
}

.fu-dc__no {
  margin-left: 4px;
  color: $fu-text-subtle;
  font-weight: $fu-fw-bold;
}

.fu-dc__muted {
  font-size: 13px;
  color: $fu-text-subtle;
}

.fu-dc__money {
  text-align: right;
}

.fu-dc__amount {
  @include fu-font-display;
  @include fu-tabular;
  font-weight: 800;
  font-size: 15px;
}

.fu-dc__status {
  min-width: 0;
}

// A long status ("Not Home. Call Again", "Appointment Scheduled") wraps to a second line rather
// than ellipsizing; past two lines it clamps, and the full text is the label's title.
.fu-dc__statusline {
  display: inline-flex;
  align-items: flex-start;
  gap: 7px;
  max-width: 100%;
  font-size: 13px;
  font-weight: $fu-fw-semibold;
  line-height: 1.35;
}

.fu-dc__statuslabel {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
  min-width: 0;
  overflow-wrap: break-word;
}

.fu-dc__dot {
  @include fu-status-dot;
  @include fu-status-dot-colors;
  margin-top: 5px; // centred on the first line (13px × 1.35)
}

.fu-dc__statussub {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  color: $fu-text-muted;
  margin: 3px 0 0 15px;
}

.fu-dc__att {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: $fu-neutral-600;
  border-radius: $fu-radius-xs;
  @include fu-focus-ring;
}

.fu-dc__more {
  @include fu-pill;
}

.fu-dc__rem {
  @include fu-tabular;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13.5px;
  font-weight: $fu-fw-semibold;
  border-radius: $fu-radius-xs;
  @include fu-focus-ring;
}

.fu-dc__clock--warn { color: $fu-warning; }
.fu-dc__clock--pending { color: $fu-text-subtle; }

.fu-dc__proc {
  display: flex;
  align-items: center;
  gap: $fu-space-2;
  flex-wrap: wrap;
}

.fu-dc__code {
  font-weight: $fu-fw-semibold;
  font-size: 12px;
  color: $fu-brand-700;
  background: $fu-brand-50;
  padding: 2px 7px;
  border-radius: $fu-radius-sm;
}
</style>
